import { deployEnvironment } from '../schemas/deploy-target';
import {
    CLIENT_SLOTS,
    ENVIRONMENT_LABEL,
    isClientSlot,
    reachableEnvironments,
    type ClientSlot,
} from '../schemas/target';

import type { ClientTargetState } from '../hooks/use-client-target';

/**
 * Which backend these screens are pointed at.
 *
 * Rendered at the top of every clients screen rather than in the app topbar:
 * the topbar is shared with Feature Flags, whose environment concept is a
 * different shape entirely, and a switcher up there would read as scoping that
 * too.
 *
 * It only ever offers what this deployment can reach, which on `stg` and `prd`
 * is exactly one thing — so there it renders nothing at all. Only the dev
 * deployment has a choice to make, between `dev` and an ephemeral slot. See
 * `schemas/deploy-target.ts`.
 *
 * ## Why it is small, and why it is still above the content
 *
 * It began as a form: a labelled select, a second one for the slot and a line
 * of prose. That is the weight of something being filled in, and this is not —
 * on the only deployment that shows it at all, the answer is `dev` on nearly
 * every visit. So it reads as a stated fact you can change, not as a step.
 *
 * Below the table would be quieter still, and wrong: it decides which rows
 * those are. Anything that scopes content has to be legible before the content,
 * or the table gets read first and attributed to the wrong backend.
 */

export function EnvironmentScope({ state }: { state: ClientTargetState }) {
    const { target, isSlotMissing } = state;
    const environments = reachableEnvironments(deployEnvironment());

    // A deployment with one reachable environment has nothing to ask. Showing
    // the answer anyway — as a one-option select, or as a badge repeating what
    // the whole app already is — spends the top of every clients screen on a
    // control that cannot be set and a fact that never changes.
    if (environments.length === 1) {
        // The one exception, and it is not a control: production stays declared
        // for the reason below, and losing the picker is precisely what removes
        // the other cue that this is production.
        return target.env === 'prd' ? (
            <div className="mb-5">
                <ProductionBand />
            </div>
        ) : null;
    }

    return (
        <div className="mb-4 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-content-muted">
                <span>Viewing</span>

                <InlineSelect
                    label="Environment"
                    value={target.env}
                    onChange={state.setEnvironment}
                    options={environments.map((env) => ({
                        value: env,
                        label: ENVIRONMENT_LABEL[env],
                    }))}
                />

                {target.env === 'eph' && (
                    <InlineSelect<ClientSlot | ''>
                        label="Slot"
                        value={target.slot ?? ''}
                        onChange={(next) => {
                            if (isClientSlot(next)) state.setSlot(next);
                        }}
                        // The empty option is not padding: `eph` with nothing
                        // chosen is a real state — the one the screens explain
                        // rather than query — and a select whose first entry is
                        // already a slot would make that state unreachable and
                        // point the screen at whichever slot happened to sort
                        // first.
                        options={[
                            { value: '', label: 'Choose a slot…' },
                            ...CLIENT_SLOTS.map((slot) => ({ value: slot, label: slot })),
                        ]}
                    />
                )}
            </div>

            {isSlotMissing && <NoSlotChosen />}
        </div>
    );
}

/**
 * A select with no field around it.
 *
 * Deliberately not `SelectField`: that primitive brings a `FieldShell` — a
 * stacked label, spacing sized for a form column — which is exactly the weight
 * this control should not carry. The label it drops is kept as `aria-label`, so
 * what is lost is the furniture and not the name.
 */
function InlineSelect<T extends string>({
    label,
    value,
    onChange,
    options,
}: {
    label: string;
    value: T;
    onChange: (next: T) => void;
    options: readonly { value: T; label: string }[];
}) {
    return (
        <select
            aria-label={label}
            value={value}
            onChange={(event) => {
                onChange(event.target.value as T);
            }}
            className="rounded-md border border-line bg-surface px-2 py-1 text-[12.5px] font-semibold text-content outline-none transition-colors hover:border-line-strong focus:border-brand focus:ring-2 focus:ring-accent-surface"
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

/**
 * Always visible, never dismissible.
 *
 * Tinting the whole screen red stops being seen within ten minutes; a thin band
 * that is always in the same place keeps reading. Same reasoning the flags
 * module records for `prd` — the widest blast radius on the ladder.
 *
 * Exported because it is the half of this file that is not a control. Screens
 * that must not offer a switcher — the client detail, where the target is a
 * property of the client being shown rather than a question — still have to
 * declare production.
 */
export function ProductionBand() {
    // `text-white` and `border-white/45` stay literal: they are the foreground
    // of a saturated red fill that does not change between themes, so theming
    // them would put near-black text on the one band that must stay loud.
    return (
        <div className="flex items-center gap-2.5 rounded-lg bg-danger-band px-4 py-2 text-white">
            <span className="rounded-full border border-white/45 px-2 py-px text-[10.5px] font-semibold uppercase tracking-wide">
                Production
            </span>
            <span className="text-[12.5px] font-semibold">
                Everything on this screen is real customer data.
            </span>
        </div>
    );
}

function NoSlotChosen() {
    return (
        <div className="rounded-lg border border-line bg-surface-sunken px-3.5 py-2.5 text-[12.5px] text-content-subtle">
            Choose a slot. Each one is its own deployment, with its own clients and its own Cognito
            pools — without one there is nothing to address.
        </div>
    );
}
